import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Popup from "../../../Popup";
import { MathJax, MathJaxContext } from "better-react-mathjax";
import showToast from "../../../utils/toast";
import BoardCascadingDropdowns from "./BoardCascadingDropdowns";
import {
  fetchQuestionTypesByLinking,
  fetchDifficultyLevels,
  addBoardQuestion,
  resetQuestionForm,
  setSelectedMedium,
  setSelectedClass,
  setSelectedSubject,
  setSelectedChapter,
  setEditData,
  setAllSelections,
} from "../redux/boardQuestionSlice";
import API_BASE_URL from "../../config";

const AddBoardQuestion = ({ isOpen, onClose, onSuccess }) => {
  const dispatch = useDispatch();
  const { boardId } = useParams();

  // Redux state
  const {
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
    linkedQuestionTypes,
    linkedQuestionTypesLoading,
    difficultyLevels,
    addingQuestion,
    editData,
  } = useSelector((state) => state.boardQuestion);

  const [isProcessing, setIsProcessing] = useState(false);

  // Regular question states
  const [questionType, setQuestionType] = useState("");
  const [questionText, setQuestionText] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);
  const [difficultyLevel, setDifficultyLevel] = useState("");

  // Comprehension passage and questions
  const [comprehensionPassage, setComprehensionPassage] = useState("");
  const [comprehensionQuestions, setComprehensionQuestions] = useState([]);

  // Current comprehension sub-question being added
  const [compQuestionType, setCompQuestionType] = useState("");
  const [compQuestionText, setCompQuestionText] = useState("");
  const [compOptions, setCompOptions] = useState(["", "", "", ""]);
  // NEW: radio UI state - syncs with * prefix in options array
  const [correctOptionIndex, setCorrectOptionIndex] = useState(null);
  const [compCorrectOptionIndex, setCompCorrectOptionIndex] = useState(null);

  // State for messages
  const [message, setMessage] = useState(null);
  const [messageType, setMessageType] = useState("info");

  // Image upload state
  const [diagramImageUrl, setDiagramImageUrl] = useState("");
  const [imageUploading, setImageUploading] = useState(false);

  //match the following
  const [matchPairsBulkInput, setMatchPairsBulkInput] = useState("");
  const [matchingPairs, setMatchingPairs] = useState([
    { column1_text: "", column2_text: "" },
  ]);

  const handleMatchPairsBulkInput = (text) => {
    setMatchPairsBulkInput(text);
    const lines = text.split(/\r?\n/);
    const parsedPairs = lines
      .map((line) => {
        const [col1, col2] = line.split(",").map((s) => s.trim());
        if (col1 && col2) {
          return { column1_text: col1, column2_text: col2 };
        }
        return null;
      })
      .filter(Boolean);

    setMatchingPairs(
      parsedPairs.length > 0
        ? parsedPairs
        : [{ column1_text: "", column2_text: "" }],
    );
  };

  function prepareForMathJax(text) {
    if (!text) return "";
    text = text.replace(
      /e\s*\^\s*\(\s*([^)]+)\s*\)/g,
      (_, exp) => `e^{${exp}}`,
    );
    text = text.replace(
      /([a-zA-Z]+|\([^)]+\))\s*\^\s*(\d+)/g,
      (_, base, p) => `${base}^{${p}}`,
    );
    return `${text}`;
  }

  // Update individual input fields
  const handleChange = (index, field, value) => {
    const updatedPairs = [...matchingPairs];
    updatedPairs[index][field] = value;
    setMatchingPairs(updatedPairs);
  };

  // Add new empty matching pair
  const addNewPair = () => {
    setMatchingPairs([
      ...matchingPairs,
      { column1_text: "", column2_text: "" },
    ]);
  };

  const addSubQuestion = () => {
    setComprehensionQuestions([...comprehensionQuestions, ""]);
  };

  const removeSubQuestion = (index) => {
    const updated = [...comprehensionQuestions];
    updated.splice(index, 1);
    setComprehensionQuestions(updated);
  };

  const handleSubQuestionChange = (index, value) => {
    const updated = [...comprehensionQuestions];
    updated[index] = value;
    setComprehensionQuestions(updated);
  };

  const resetForm = () => {
    setMatchingPairs([{ column1_text: "", column2_text: "" }]);
    setQuestionText("");
    setOptions(["", "", "", ""]);
    setQuestionType(
      linkedQuestionTypes.length > 0 ? linkedQuestionTypes[0].type_name : "",
    );
    setDifficultyLevel(
      difficultyLevels.length > 0 ? difficultyLevels[0].difficulty_name : "",
    );
    setComprehensionPassage("");
    setComprehensionQuestions([]);
    setCompQuestionText("");
    setCompOptions(["", "", "", ""]);
    setCorrectOptionIndex(null);
    setCompCorrectOptionIndex(null);
    setCompQuestionType(
      linkedQuestionTypes.length > 0 ? linkedQuestionTypes[0].type_name : "",
    );
    setDiagramImageUrl("");
    setImageUploading(false);
    setMessage(null);
    setMessageType("info");
    // dispatch(resetQuestionForm()); // Clear medium, class, subject, chapter selections
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const ACCEPTED_TYPES = [
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];
      const MAX_SIZE = 1 * 1024 * 1024;

      if (!ACCEPTED_TYPES.includes(file.type)) {
        showToast.error("Only JPG, JPEG, PNG, or WEBP images are allowed.");
        return;
      }

      if (file.size > MAX_SIZE) {
        showToast.error(
          "Recommended maximum size is 1MB. Please compress the image.",
        );
        return;
      }

      setImageUploading(true);

      const formData = new FormData();
      formData.append("image", file);

      try {
        const response = await fetch(
          `https://web1024.docmaster.in:5007/upload`,
          {
            method: "POST",
            body: formData,
          },
        );

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(
            errorData.message || `HTTP error! Status: ${response.status}`,
          );
        }

        const result = await response.json();
        setDiagramImageUrl(result.uploaded);
        showToast.success("Image uploaded successfully!");
      } catch (err) {
        console.error("Error uploading image:", err);
        showToast.error(`Failed to upload image: ${err.message}`);
        setDiagramImageUrl("");
      } finally {
        setImageUploading(false);
      }
    }
  };

  // Fetch initial data
  useEffect(() => {
    if (isOpen && boardId) {
      dispatch(fetchDifficultyLevels());
    }
  }, [isOpen, boardId, dispatch]);

  // Fetch linked question types when subject is selected
  useEffect(() => {
    if (selectedSubject && selectedMedium && selectedClass && boardId) {
      dispatch(
        fetchQuestionTypesByLinking({
          board_id: boardId,
          medium_id: selectedMedium,
          class_id: selectedClass,
          subject_id: selectedSubject,
        }),
      );
    }
  }, [selectedSubject, selectedMedium, selectedClass, boardId, dispatch]);

  // Set default values when data is loaded
  useEffect(() => {
    if (linkedQuestionTypes.length > 0 && !questionType) {
      setQuestionType(linkedQuestionTypes[0].type_name);
      setCompQuestionType(linkedQuestionTypes[0].type_name);
    }
  }, [linkedQuestionTypes]);

  useEffect(() => {
    if (difficultyLevels.length > 0 && !difficultyLevel) {
      setDifficultyLevel(difficultyLevels[0].difficulty_name);
    }
  }, [difficultyLevels]);

  // Pre-fill form when editData is passed
  useEffect(() => {
    if (editData) {
      console.log("Edit data:", editData);

      // Auto-select cascading dropdowns
      dispatch(
        setAllSelections({
          medium: editData.medium_id ? String(editData.medium_id) : null,
          classId: editData.class_id ? String(editData.class_id) : null,
          subject: editData.subject_id ? String(editData.subject_id) : null,
          chapter: editData.chapter_id ? String(editData.chapter_id) : null,
        }),
      );

      setQuestionType(editData.type_name || "");
      setQuestionText(editData.question_text || "");
      if (editData.options && editData.options.length > 0) {
        // Build string options with * prefix for correct answer (existing convention)
        const builtOptions = editData.options.map((opt) =>
          opt.is_correct ? `*${opt.text}` : opt.text
        );
        setOptions(builtOptions);
        // Also set radio index for NEW radio UI
        const correctIdx = editData.options.findIndex(
          (opt) => opt.is_correct === 1 || opt.is_correct === true || opt.is_correct === "1"
        );
        setCorrectOptionIndex(correctIdx >= 0 ? correctIdx : null);
      } else {
        setOptions(["", "", "", ""]);
        setCorrectOptionIndex(null);
      }
      if (editData.matching_options && editData.matching_options.length > 0) {
        setMatchingPairs(
          editData.matching_options.map((pair) => ({
            column1_text: pair.column1_text,
            column2_text: pair.column2_text,
          })),
        );
      }
      setDifficultyLevel(editData.difficulty_name || "");
      setComprehensionPassage(editData.passage_text || "");
      setComprehensionQuestions(editData.subQuestions || []);
      if (editData.images && editData.images.length > 0) {
        setDiagramImageUrl(editData.images[0].image_url || "");
      } else {
        setDiagramImageUrl("");
      }
    }
  }, [editData]);

  // Helper to process options (mark correct with '*')
  const processOptions = (rawOptions) => {
    return rawOptions.map((opt) => {
      const isCorrect = opt.startsWith("*");
      const text = isCorrect ? opt.substring(1).trim() : opt.trim();
      return { text: text || "", is_correct: isCorrect };
    });
  };

  // Helper to get type ID from type name
  const getQuestionTypeId = (typeName) => {
    const type = linkedQuestionTypes.find((q) => q.type_name === typeName);
    return type ? type.type_id : null;
  };

  // Helper to get difficulty ID from level name
  const getDifficultyId = (levelName) => {
    const difficulty = difficultyLevels.find(
      (d) => d.difficulty_name === levelName,
    );
    return difficulty ? difficulty.difficulty_id : null;
  };

  // Handlers for input changes
  const handleOptionChange = (index, value) => {
    const newOptions = [...options];
    newOptions[index] = value;
    setOptions(newOptions);
  };

  const handleCompOptionChange = (index, value) => {
    const updated = [...compOptions];
    updated[index] = value;
    setCompOptions(updated);
  };

  // NEW: Radio circle handlers — update index state AND sync * prefix in options array
  const handleCorrectOptionSelect = (index) => {
    setCorrectOptionIndex(index);
    // Sync * prefix into options array (existing processOptions convention)
    setOptions((prev) =>
      prev.map((opt, i) => {
        const clean = opt.startsWith("*") ? opt.substring(1) : opt;
        return i === index ? `*${clean}` : clean;
      })
    );
  };

  const handleCompCorrectOptionSelect = (index) => {
    setCompCorrectOptionIndex(index);
    // Sync * prefix into compOptions array
    setCompOptions((prev) =>
      prev.map((opt, i) => {
        const clean = opt.startsWith("*") ? opt.substring(1) : opt;
        return i === index ? `*${clean}` : clean;
      })
    );
  };

  // Add sub-question for comprehension
  const handleAddCompQuestion = () => {
    if (compQuestionText.trim() === "") {
      showToast.error("Please enter sub-question text.");
      return;
    }

    const processedCompOptions = processOptions(compOptions);
    if (
      compQuestionType === "Multiple Choice Questions" ||
      compQuestionType === "Multiple Choice Multiple Correct" ||
      compQuestionType === "बहुविकल्पीय प्रश्न"
    ) {
      if (compOptions.every((opt) => opt.trim() === "")) {
        showToast.error("Please enter options for the sub-question.");
        return;
      }
      if (compCorrectOptionIndex === null) {
        showToast.error("Please select the correct answer by clicking the circle.");
        return;
      }
    }

    const newCompQ = {
      id: Date.now(),
      type: compQuestionType,
      question: compQuestionText.trim(),
      options:
        compQuestionType === "Multiple Choice Questions" ||
        compQuestionType === "Multiple Choice Multiple Correct" ||
        compQuestionType === "बहुविकल्पीय प्रश्न"
          ? processedCompOptions
          : [],
      marks: 1,
    };

    setComprehensionQuestions((prev) => [...prev, newCompQ]);
    setCompQuestionText("");
    setCompOptions(["", "", "", ""]);
    setCompCorrectOptionIndex(null);
    setCompQuestionType(
      linkedQuestionTypes.length > 0 ? linkedQuestionTypes[0].type_name : "",
    );
    showToast.success("Sub-question added successfully!");
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const selectedQuestionTypeId = getQuestionTypeId(questionType);
    const selectedDifficultyId = getDifficultyId(difficultyLevel);

    if (!selectedQuestionTypeId || !selectedDifficultyId) {
      showToast.error(
        "Please select valid Question Type and Difficulty Level.",
      );
      return;
    }

    if (!selectedClass || !selectedSubject || !selectedChapter) {
      showToast.error("Please select Class, Subject, and Chapter.");
      return;
    }

    const payloadBase = {
      board_id: parseInt(boardId),
      medium_id: parseInt(selectedMedium),
      questionType: selectedQuestionTypeId,
      difficulty_id: selectedDifficultyId,
      created_by: 1,
      class_id: parseInt(selectedClass),
      subject_id: parseInt(selectedSubject),
      chapter_id: parseInt(selectedChapter),
      images: diagramImageUrl ? [diagramImageUrl] : [],
    };

    let finalPayload;
    const toastId = showToast.loading("Processing...");

    if (questionType === "Comprehension Parent") {
      if (!comprehensionPassage.trim() || comprehensionQuestions.length === 0) {
        showToast.dismiss(toastId);
        showToast.error("Enter passage and at least one sub-question.");
        return;
      }

      finalPayload = {
        ...payloadBase,
        question_text: questionText.trim(),
        passage_text: comprehensionPassage.trim(),
        group_stat: "parent",
        marks: 0,
        subQuestions: comprehensionQuestions.map((q) => ({
          question_text: q.question,
          questionType: getQuestionTypeId(q.type),
          options: q.options,
          difficulty_id: selectedDifficultyId,
          marks: q.marks || 1,
          group_stat: "child",
          link_id: null,
        })),
      };
    } else if (questionType === "Comprehension") {
      if (!comprehensionPassage.trim() || comprehensionQuestions.length === 0) {
        showToast.dismiss(toastId);
        showToast.error("Enter passage and at least one sub-question.");
        return;
      }

      finalPayload = {
        ...payloadBase,
        question_text: "",
        passage_text: comprehensionPassage.trim(),
        group_stat: null,
        marks: 0,
        subQuestions: comprehensionQuestions.map((q) => ({
          question_text: typeof q === "string" ? q : q.question_text,
          questionType: getQuestionTypeId("Short Answer"),
          options: [],
          difficulty_id: selectedDifficultyId,
          marks: 1,
          group_stat: "child",
          link_id: null,
        })),
      };
    } else if (
      questionType === "Match the Following" ||
      questionType === "मिलान करे"
    ) {
      const validPairs = matchingPairs.filter(
        (pair) => pair.column1_text.trim() && pair.column2_text.trim(),
      );
      if (validPairs.length === 0) {
        showToast.dismiss(toastId);
        showToast.error("Please add valid matching pairs.");
        return;
      }

      finalPayload = {
        ...payloadBase,
        question_text: questionText.trim(),
        questionType: 16,
        matching_options: validPairs.map((pair, idx) => ({
          column1_text: pair.column1_text.trim(),
          column2_text: pair.column2_text.trim(),
          match_sort_order: idx + 1,
        })),
      };
    } else {
      const processedOpts = processOptions(options);
      if (
        questionType === "Multiple Choice Questions" ||
        questionType === "Multiple Choice Multiple Correct" ||
        questionType === "बहुविकल्पीय प्रश्न"
      ) {
        if (options.every((opt) => opt.trim() === "")) {
          showToast.dismiss(toastId);
          showToast.error("Please enter options.");
          return;
        }

        if (correctOptionIndex === null) {
          showToast.dismiss(toastId);
          showToast.error("Please select the correct answer by clicking the circle.");
          return;
        }
      }

      finalPayload = {
        ...payloadBase,
        question_text: questionText.trim(),
        options: processedOpts,
        marks: 1,
        passage_text: null,
        group_stat: null,
        link_id: null,
      };
    }

    try {
      setIsProcessing(true);

      if (editData?.question_id) {
        // Update existing question
        const response = await fetch(
          `${API_BASE_URL}/questions/${editData.question_id}`,
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              ...finalPayload,
              question_id: editData.question_id,
              updated_by: 1,
            }),
          },
        );
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || "Failed to update question");
        }
        dispatch(setEditData(null));
        showToast.dismiss(toastId);
        showToast.success("Question updated successfully!");
      } else {
        // Add new question
        await dispatch(addBoardQuestion(finalPayload)).unwrap();
        showToast.dismiss(toastId);
        showToast.success("Question added successfully!");
      }

      // Call onSuccess callback to refresh questions list
      if (onSuccess) {
        onSuccess();
      }

      resetForm();
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error("Error:", err);
      showToast.dismiss(toastId);
      showToast.error(
        editData?.question_id
          ? "Failed to update question."
          : "Failed to add question.",
      );
    } finally {
      setIsProcessing(false);
    }
  };

  if (!isOpen) return null;

  const canShowQuestionForm =
    selectedMedium && selectedClass && selectedSubject && selectedChapter;

  return (
    <>
      <style>
        {`
          .form-card {
            background: white;
            border-radius: 8px;
            overflow: hidden;
          }

          .form-body {
            padding: 20px;
          }

          .section-card {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 18px;
            margin-bottom: 18px;
            border: 1px solid #e0e0e0;
            transition: all 0.2s ease;
          }

          .section-card:hover {
            box-shadow: 0 2px 8px rgba(0,0,0,0.08);
          }

          .section-title {
            font-size: 15px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #3498db;
          }

          .form-label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 6px;
            display: block;
            font-size: 13px;
          }

          .form-input, .form-select, .form-textarea {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            transition: all 0.2s ease;
            background: white;
            height: 38px;
          }

          .form-input:focus, .form-select:focus, .form-textarea:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
          }

          .form-textarea {
            resize: vertical;
            min-height: 80px;
            height: auto;
          }

          .btn-primary {
            background: #3498db;
            color: white;
            border: none;
            padding: 9px 24px;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
            height: 38px;
          }

          .btn-primary:hover:not(:disabled) {
            background: #2980b9;
          }

          .btn-primary:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .btn-secondary {
            background: #95a5a6;
            color: white;
            border: none;
            padding: 9px 24px;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 14px;
            height: 38px;
          }

          .btn-secondary:hover {
            background: #7f8c8d;
          }

          .btn-success {
            background: #27ae60;
            color: white;
            border: none;
            padding: 8px 18px;
            border-radius: 6px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 13px;
            height: 36px;
          }

          .btn-success:hover {
            background: #229954;
          }

          .btn-danger {
            background: #e74c3c;
            color: white;
            border: none;
            padding: 6px 12px;
            border-radius: 5px;
            font-weight: 500;
            cursor: pointer;
            transition: all 0.2s ease;
            font-size: 13px;
            height: 34px;
          }

          .btn-danger:hover {
            background: #c0392b;
          }

          .options-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
            margin-top: 8px;
          }

          .image-preview {
            margin-top: 12px;
            padding: 12px;
            background: #f8f9fa;
            border-radius: 6px;
            text-align: center;
          }

          .image-preview img {
            max-width: 250px;
            border-radius: 6px;
            box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          }

          .sub-questions-list {
            background: white;
            border-radius: 6px;
            padding: 15px;
            margin-top: 15px;
          }

          .sub-question-item {
            padding: 12px;
            background: #f8f9fa;
            border-left: 3px solid #3498db;
            margin-bottom: 8px;
            border-radius: 4px;
            font-size: 13px;
          }

          .math-preview {
            background: #fff9e6;
            border: 1px solid #ffc107;
            border-radius: 6px;
            padding: 12px;
            margin-top: 12px;
            font-size: 13px;
          }

          .required {
            color: #e74c3c;
            font-weight: 600;
          }

          .divider {
            height: 1px;
            background: #e0e0e0;
            margin: 20px 0;
          }

          .action-buttons {
            display: flex;
            gap: 12px;
            justify-content: flex-end;
            margin-top: 20px;
            padding-top: 15px;
            border-top: 1px solid #e0e0e0;
          }

          @media (max-width: 768px) {
            .options-grid {
              grid-template-columns: 1fr;
            }
          }

          /* NEW: Radio circle correct-answer selector */
          .option-row {
            display: flex;
            align-items: center;
            gap: 8px;
            margin-bottom: 10px;
          }

          .option-radio-circle {
            width: 22px;
            height: 22px;
            border-radius: 50%;
            border: 2px solid #9ca3af;
            background: white;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
            transition: border-color 0.2s ease, background 0.2s ease;
            padding: 0;
          }

          .option-radio-circle.selected {
            border-color: #10b981;
            background: #10b981;
            color: white;
            font-size: 13px;
            font-weight: 700;
          }

          .option-radio-dot {
            width: 8px;
            height: 8px;
            border-radius: 50%;
            background: white;
            display: block;
          }

          .option-badge {
            font-weight: 600;
            font-size: 13px;
            color: #374151;
            width: 20px;
            flex-shrink: 0;
          }

          .option-row .form-input.correct-input {
            border-color: #10b981;
            background: #f0fdf4;
          }

          .options-hint {
            font-size: 12px;
            color: #6b7280;
            margin-bottom: 8px;
          }
        `}
      </style>

      <Popup
        title={editData ? "Edit Question" : "Add New Question"}
        onClose={() => {
          resetForm();
          if (onClose) onClose();
        }}
        maxWidth="950px"
      >
        <div className="form-card">
          <div className="form-body">
            <form onSubmit={handleSubmit}>
              {/* Cascading Dropdowns */}
              <BoardCascadingDropdowns
                boardId={boardId}
                showTitle={true}
                disabled={!!editData}
              />

              {/* Question Details Form */}
              {canShowQuestionForm && (
                <>
                  {/* Show message if no question types found */}
                  {linkedQuestionTypes.length === 0 &&
                    !linkedQuestionTypesLoading && (
                      <div
                        style={{
                          padding: "15px",
                          background: "#fff3cd",
                          border: "1px solid #ffc107",
                          borderRadius: "6px",
                          marginBottom: "15px",
                          textAlign: "center",
                        }}
                      >
                        <p
                          style={{
                            margin: 0,
                            color: "#856404",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          ⚠️ No question types found for this subject. Please
                          contact admin to link question types.
                        </p>
                      </div>
                    )}

                  <div className="section-card">
                    <div className="section-title">Question Configuration</div>

                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label className="form-label">
                          Question Type <span className="required">*</span>
                        </label>
                        <select
                          value={questionType}
                          onChange={(e) => setQuestionType(e.target.value)}
                          className="form-select"
                          disabled={
                            linkedQuestionTypesLoading ||
                            linkedQuestionTypes.length === 0
                          }
                        >
                          {linkedQuestionTypesLoading ? (
                            <option value="">Loading question types...</option>
                          ) : linkedQuestionTypes.length === 0 ? (
                            <option value="">
                              No question types found for this subject
                            </option>
                          ) : (
                            linkedQuestionTypes.map((type) => (
                              <option key={type.type_id} value={type.type_name}>
                                {type.type_name
                                  .replace(/([A-Z])/g, " $1")
                                  .trim()}
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div className="col-md-6">
                        <label className="form-label">
                          Difficulty Level <span className="required">*</span>
                        </label>
                        <select
                          value={difficultyLevel}
                          onChange={(e) => setDifficultyLevel(e.target.value)}
                          className="form-select"
                        >
                          {difficultyLevels.map((level) => (
                            <option
                              key={level.difficulty_id}
                              value={level.difficulty_name}
                            >
                              {level.difficulty_name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Diagram Image (Optional)
                      </label>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageUpload}
                        disabled={imageUploading}
                        className="form-input"
                      />
                      {diagramImageUrl && (
                        <div className="image-preview">
                          <p
                            style={{
                              margin: "0 0 10px 0",
                              fontWeight: 600,
                              fontSize: "13px",
                            }}
                          >
                            Image Preview:
                          </p>
                          <img src={diagramImageUrl} alt="Diagram Preview" />
                        </div>
                      )}
                      {imageUploading && (
                        <p
                          style={{
                            marginTop: "10px",
                            color: "#3498db",
                            fontSize: "13px",
                          }}
                        >
                          Uploading image...
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Question Type Specific Forms */}
                  {questionType === "Comprehension Parent" ? (
                    <div className="section-card">
                      <div className="section-title">Comprehension Details</div>

                      <div className="mb-3">
                        <label className="form-label">
                          Comprehension Title (Optional)
                        </label>
                        <input
                          type="text"
                          value={questionText}
                          onChange={(e) => setQuestionText(e.target.value)}
                          placeholder="Enter a title or summary for the passage"
                          className="form-input"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Passage <span className="required">*</span>
                        </label>
                        <textarea
                          value={comprehensionPassage}
                          onChange={(e) =>
                            setComprehensionPassage(e.target.value)
                          }
                          placeholder="Enter comprehension passage"
                          rows={6}
                          className="form-textarea"
                        />
                      </div>

                      <div className="divider"></div>

                      <h4
                        style={{
                          textAlign: "center",
                          color: "#2c3e50",
                          marginBottom: "15px",
                          fontSize: "15px",
                          fontWeight: "600",
                        }}
                      >
                        Add Sub-Questions
                      </h4>

                      <div className="mb-3">
                        <label className="form-label">Sub-Question Type</label>
                        <select
                          value={compQuestionType}
                          onChange={(e) => setCompQuestionType(e.target.value)}
                          className="form-select"
                          disabled={
                            linkedQuestionTypesLoading ||
                            linkedQuestionTypes.length === 0
                          }
                        >
                          {linkedQuestionTypesLoading ? (
                            <option value="">Loading question types...</option>
                          ) : linkedQuestionTypes.length === 0 ? (
                            <option value="">No question types found</option>
                          ) : (
                            linkedQuestionTypes.map((type) => (
                              <option key={type.type_id} value={type.type_name}>
                                {type.type_name
                                  .replace(/([A-Z])/g, " $1")
                                  .trim()}
                              </option>
                            ))
                          )}
                        </select>
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Sub-Question Text</label>
                        <input
                          type="text"
                          value={compQuestionText}
                          onChange={(e) => setCompQuestionText(e.target.value)}
                          placeholder="Enter sub-question"
                          className="form-input"
                        />
                      </div>

                      {(compQuestionType === "Multiple Choice Questions" ||
                        compQuestionType ===
                          "Multiple Choice Multiple Correct" ||
                        compQuestionType === "बहुविकल्पीय प्रश्न") && (
                        <div className="mb-3">
                          <label className="form-label">
                            Options <span className="required">*</span>
                          </label>
                          <p className="options-hint">
                            Click the circle to mark the correct answer
                          </p>
                          <div className="options-grid">
                            {compOptions.map((option, index) => {
                            const isSelected = compCorrectOptionIndex === index;
                            const letter = String.fromCharCode(65 + index);
                            const displayVal = option.startsWith("*")
                              ? option.substring(1)
                              : option;
                            return (
                              <div key={index} className="option-row">
                                <button
                                  type="button"
                                  className={`option-radio-circle${isSelected ? " selected" : ""}`}
                                  onClick={() => handleCompCorrectOptionSelect(index)}
                                  title={isSelected ? `Option ${letter} is Correct` : `Mark Option ${letter} as Correct`}
                                >
                                  {isSelected ? "✓" : ""}
                                </button>
                                <span className="option-badge">{letter}.</span>
                                <input
                                  type="text"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleCompOptionChange(index, e.target.value)
                                  }
                                  placeholder={`Option ${letter}`}
                                  className={`form-input${isSelected ? " correct-input" : ""}`}
                                  style={{ flex: 1 }}
                                />
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      )}

                      <button
                        type="button"
                        onClick={handleAddCompQuestion}
                        className="btn-success"
                      >
                        + Add Sub-Question
                      </button>

                      {comprehensionQuestions.length > 0 && (
                        <div className="sub-questions-list">
                          <strong
                            style={{ fontSize: "14px", color: "#2c3e50" }}
                          >
                            Added Sub-Questions ({comprehensionQuestions.length}
                            ):
                          </strong>
                          {comprehensionQuestions.map((subQ, idx) => (
                            <div key={subQ.id} className="sub-question-item">
                              <strong>
                                {idx + 1}. (
                                {subQ.type.replace(/([A-Z])/g, " $1").trim()}):
                              </strong>{" "}
                              {subQ.question}
                              {subQ.options.length > 0 && (
                                <ul
                                  style={{
                                    marginTop: "8px",
                                    paddingLeft: "20px",
                                  }}
                                >
                                  {subQ.options.map((opt, optIndex) => (
                                    <li
                                      key={optIndex}
                                      style={{
                                        color: opt.is_correct
                                          ? "#27ae60"
                                          : "#6c757d",
                                        fontWeight: opt.is_correct
                                          ? "600"
                                          : "normal",
                                        fontSize: "13px",
                                      }}
                                    >
                                      {opt.text} {opt.is_correct && "(Correct)"}
                                    </li>
                                  ))}
                                </ul>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ) : questionType === "Comprehension" ? (
                    <div className="section-card">
                      <div className="section-title">Comprehension Passage</div>

                      <div className="mb-3">
                        <label className="form-label">
                          Comprehension Passage{" "}
                          <span className="required">*</span>
                        </label>
                        <textarea
                          value={comprehensionPassage}
                          onChange={(e) =>
                            setComprehensionPassage(e.target.value)
                          }
                          placeholder="Enter comprehension passage"
                          rows={6}
                          className="form-textarea"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">Sub-Questions:</label>
                        {comprehensionQuestions.map((subQ, index) => (
                          <div
                            key={index}
                            style={{
                              display: "flex",
                              gap: "10px",
                              marginBottom: "10px",
                            }}
                          >
                            <input
                              type="text"
                              value={
                                typeof subQ === "string"
                                  ? subQ
                                  : subQ.question_text
                              }
                              onChange={(e) =>
                                handleSubQuestionChange(index, e.target.value)
                              }
                              placeholder={`Sub-question ${index + 1}`}
                              className="form-input"
                              style={{ flex: 1 }}
                            />
                            <button
                              type="button"
                              onClick={() => removeSubQuestion(index)}
                              className="btn-danger"
                            >
                              ✕
                            </button>
                          </div>
                        ))}

                        <button
                          type="button"
                          onClick={addSubQuestion}
                          className="btn-success"
                          style={{ marginTop: "10px" }}
                        >
                          + Add Sub-Question
                        </button>
                      </div>
                    </div>
                  ) : questionType === "Match the Following" ||
                    questionType === "मिलान करे" ? (
                    <div className="section-card">
                      <div className="section-title">Match the Following</div>

                      <div className="mb-3">
                        <label className="form-label">Question Text</label>
                        <input
                          type="text"
                          value={questionText}
                          onChange={(e) => setQuestionText(e.target.value)}
                          placeholder="Enter your question..."
                          className="form-input"
                        />
                      </div>

                      <div className="mb-3">
                        <label className="form-label">
                          Bulk Input (CSV format - optional)
                        </label>
                        <textarea
                          value={matchPairsBulkInput}
                          onChange={(e) =>
                            handleMatchPairsBulkInput(e.target.value)
                          }
                          placeholder="Paste values like:&#10;apple, fruit&#10;bike, vehicle"
                          rows={4}
                          className="form-textarea"
                        />
                      </div>

                      <label className="form-label">Matching Pairs:</label>
                      {matchingPairs.map((pair, index) => (
                        <div
                          key={index}
                          style={{
                            display: "flex",
                            gap: "10px",
                            marginBottom: "10px",
                          }}
                        >
                          <input
                            type="text"
                            placeholder={`Column A - ${index + 1}`}
                            value={pair.column1_text}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "column1_text",
                                e.target.value,
                              )
                            }
                            className="form-input"
                            style={{ flex: 1 }}
                          />
                          <input
                            type="text"
                            placeholder={`Column B - ${index + 1}`}
                            value={pair.column2_text}
                            onChange={(e) =>
                              handleChange(
                                index,
                                "column2_text",
                                e.target.value,
                              )
                            }
                            className="form-input"
                            style={{ flex: 1 }}
                          />
                        </div>
                      ))}

                      <button
                        type="button"
                        onClick={addNewPair}
                        className="btn-success"
                      >
                        + Add Pair
                      </button>
                    </div>
                  ) : (
                    <div className="section-card">
                      <div className="section-title">Question Content</div>

                      <div className="mb-3">
                        <label className="form-label">
                          Question Text <span className="required">*</span>
                        </label>
                        <textarea
                          value={questionText}
                          onChange={(e) => setQuestionText(e.target.value)}
                          placeholder="Enter question text"
                          rows={4}
                          className="form-textarea"
                        />
                        {(questionText.includes("$") ||
                          questionText.includes("\\(") ||
                          questionText.includes("\\[")) && (
                          <MathJaxContext>
                            <div className="math-preview">
                              <strong style={{ fontSize: "13px" }}>
                                Math Preview:
                              </strong>
                              {questionText.trim() ? (
                                <MathJax dynamic>
                                  <div style={{ marginTop: "8px" }}>
                                    {prepareForMathJax(questionText)}
                                  </div>
                                </MathJax>
                              ) : (
                                <div style={{ fontSize: "13px" }}>
                                  No preview
                                </div>
                              )}
                            </div>
                          </MathJaxContext>
                        )}
                      </div>

                      {(questionType === "Multiple Choice Questions" ||
                        questionType === "Multiple Choice Multiple Correct" ||
                        questionType === "बहुविकल्पीय प्रश्न") && (
                        <div className="mb-3">
                          <label className="form-label">
                            Options <span className="required">*</span>
                          </label>
                          <p className="options-hint">
                            Click the circle to mark the correct answer
                          </p>
                          <div className="options-grid">
                            {options.map((option, index) => {
                            const isSelected = correctOptionIndex === index;
                            const letter = String.fromCharCode(65 + index);
                            const displayVal = option.startsWith("*")
                              ? option.substring(1)
                              : option;
                            return (
                              <div key={index} className="option-row">
                                <button
                                  type="button"
                                  className={`option-radio-circle${isSelected ? " selected" : ""}`}
                                  onClick={() => handleCorrectOptionSelect(index)}
                                  title={isSelected ? `Option ${letter} is Correct` : `Mark Option ${letter} as Correct`}
                                >
                                  {isSelected ? "✓" : ""}
                                </button>
                                <span className="option-badge">{letter}.</span>
                                <input
                                  type="text"
                                  value={displayVal}
                                  onChange={(e) =>
                                    handleOptionChange(index, e.target.value)
                                  }
                                  placeholder={`Option ${letter}`}
                                  className={`form-input${isSelected ? " correct-input" : ""}`}
                                  style={{ flex: 1 }}
                                />
                              </div>
                            );
                          })}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}

              <div className="action-buttons">
                <button
                  type="button"
                  onClick={() => {
                    resetForm();
                    if (onClose) onClose();
                  }}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canShowQuestionForm || isProcessing}
                  className="btn-primary"
                >
                  {editData
                    ? isProcessing
                      ? "Updating..."
                      : "Update Question"
                    : isProcessing
                      ? "Adding..."
                      : "Add Question"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Popup>
    </>
  );
};

export default AddBoardQuestion;
